import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Search, Cloud, X, Loader2 } from 'lucide-react';

/**
 * Seletor de cliente com busca assíncrona (não carrega lista inteira).
 * Opcional: importar pelo código M8 (PessoaId).
 */
const ClienteSelector = ({
  value,
  onChange,
  excludeIds = [],
  placeholder = 'Digite nome, código ou M8 ID...',
  className = '',
  showM8Import = true,
  required = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [importingM8, setImportingM8] = useState(false);
  const [m8Codigo, setM8Codigo] = useState('');
  const [showM8Input, setShowM8Input] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected?.id === value) return;
    api.get(`/clientes/${value}`)
      .then((r) => setSelected(r.data))
      .catch(() => setSelected(null));
  }, [value, selected?.id]);

  useEffect(() => {
    if (selected) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/clientes', { params: { search: q, limit: 20 } });
        const list = (res.data.clientes || []).filter((c) => !excludeIds.includes(c.id));
        setResults(list);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, excludeIds, selected]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectCliente = (cliente) => {
    setSelected(cliente);
    setQuery('');
    setOpen(false);
    onChange?.(cliente);
  };

  const clearSelection = () => {
    setSelected(null);
    setQuery('');
    onChange?.(null);
  };

  const importarM8 = async () => {
    const cod = m8Codigo.trim();
    if (!cod) {
      toast.error('Informe o código M8');
      return;
    }
    setImportingM8(true);
    try {
      const res = await api.post('/clientes/m8/import', { m8_codigo: cod }, { timeout: 180000 });
      if (res.data.warning || res.data.vinculo_os_completo === false) {
        toast(
          `${res.data.nome} importado. ${res.data.warning || 'Alguns dados M8 ficaram incompletos.'}`,
          { icon: '⚠️', duration: 8000 }
        );
      } else {
        toast.success(`Importado: ${res.data.nome}`);
      }
      selectCliente(res.data);
      setShowM8Input(false);
      setM8Codigo('');
    } catch (err) {
      const isNetwork =
        !err.response ||
        err.code === 'ECONNABORTED' ||
        err.code === 'ERR_NETWORK' ||
        err.message?.includes('Network Error');
      if (isNetwork) {
        // Proxy costuma cortar a resposta, mas o backend já gravou o cliente.
        try {
          const { data } = await api.get('/clientes', { params: { search: cod, limit: 5 } });
          const encontrado = (data.clientes || []).find(
            (c) => String(c.codigo) === cod || String(c.m8_cliente_id) === cod
          );
          if (encontrado) {
            toast.success(
              `Importado: ${encontrado.nome} (a conexão demorou, mas o cliente foi gravado)`
            );
            selectCliente(encontrado);
            setShowM8Input(false);
            setM8Codigo('');
            return;
          }
        } catch (_) {
          /* segue para erro abaixo */
        }
      }
      toast.error(err.response?.data?.detail || 'Erro ao importar do M8');
    } finally {
      setImportingM8(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {selected ? (
        <div className="flex items-center gap-2 w-full px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50">
          <span className="flex-1 text-sm text-slate-900 truncate">
            {selected.codigo} — {selected.nome}
            {selected.avulso ? ' (avulso)' : ''}
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-slate-500 hover:text-slate-700 p-1"
            title="Limpar seleção"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => query.trim().length >= 2 && setOpen(true)}
            placeholder={placeholder}
            className="w-full pl-10 pr-10 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-sm"
            required={required}
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
          )}
        </div>
      )}

      {open && !selected && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg">
          {results.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => selectCliente(c)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-emerald-50 transition-colors"
              >
                <span className="font-medium text-slate-900">{c.codigo}</span>
                <span className="text-slate-600"> — {c.nome}</span>
                {c.avulso && (
                  <span className="ml-1 text-[11px] text-amber-700">avulso</span>
                )}
                {c.cidade && (
                  <span className="text-slate-400 text-xs block">{c.cidade}{c.estado ? `/${c.estado}` : ''}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && !selected && !loading && query.trim().length >= 2 && results.length === 0 && (
        <p className="absolute z-20 mt-1 w-full px-4 py-2 text-sm text-slate-500 bg-white border border-slate-200 rounded-lg shadow">
          Nenhum cliente encontrado
        </p>
      )}

      {showM8Import && (
        <div className="mt-2">
          {!showM8Input ? (
            <button
              type="button"
              onClick={() => setShowM8Input(true)}
              className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1"
            >
              <Cloud className="w-3.5 h-3.5" />
              Importar do M8 por código
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={m8Codigo}
                onChange={(e) => setM8Codigo(e.target.value)}
                placeholder="Código M8 (PessoaId)"
                className="flex-1 min-w-[120px] px-3 py-1.5 text-sm rounded-lg border border-violet-200 focus:border-violet-500 outline-none"
              />
              <button
                type="button"
                onClick={importarM8}
                disabled={importingM8}
                className="text-sm bg-violet-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-50 flex items-center gap-1"
              >
                {importingM8 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />}
                Importar
              </button>
              <button
                type="button"
                onClick={() => { setShowM8Input(false); setM8Codigo(''); }}
                className="text-sm text-slate-500 px-2"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClienteSelector;
